import React, { useRef, useState, useEffect } from 'react';
import './About.css';
import { motion } from 'framer-motion';
import { useCountUp } from '../../hooks/useCountUp';

const heroImage = require('../../assets/images/naf-headshot.jpg');

const About: React.FC = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.8,
      }
    })
  };

  // Track if the stats section is in view
  const [isStatsInView, setIsStatsInView] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  // Setup intersection observer to detect when stats are in view
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsStatsInView(true);
          // Once we've seen it, we can disconnect the observer
          observer.disconnect();
        }
      });
    }, { threshold: 0.1 }); // Trigger when at least 10% is visible

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  // Use our custom hook for each stat
  const studentsCount = useCountUp({ end: 500, isInView: isStatsInView });
  const acceptanceRate = useCountUp({ end: 95, isInView: isStatsInView });
  const yearsExperience = useCountUp({ end: 10, isInView: isStatsInView });

  return (
    <section id="about" className="about">
      <div className="container">
        <motion.div 
          className="section-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
          variants={fadeIn}
        >
          <h2>About Naf Med Prep</h2>
          <p className="section-subtitle">Helping pre-medical students achieve their dreams</p>
        </motion.div>

        <div className="about-content">
          <motion.div 
            className="about-image-container"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
            variants={fadeIn}
          >
            <div className="image-placeholder">
                <img src={heroImage} alt="Nafis Eghrari" className="about-image" />
            </div>
            <h3>Your Partner in Pre-Med Success</h3>
          </motion.div>

          <motion.div 
            className="about-text-container"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
            variants={fadeIn}
          >
            <div className="about-text-content">
                <p>Hi everyone! My name is Nafis Eghrari, MD, and I'm the founder of NafMedPrep. I was born and raised in Scottsdale, Arizona, and completed my undergraduate degree in Biomedical Sciences at Arizona State University. I earned my medical degree at Case Western Reserve University School of Medicine (Class of 2026). I am currently a Physical Medicine & Rehabilitation Resident Physician at UT Southwestern Medical Center.</p>
                
                <p>As an undergraduate, I struggled to find effective premed advising and found that most admissions-prep services were prohibitively expensive. With no family members in medicine, I faced the application process largely on my own. I relied on my writing skills and support from friends and mentors to build my AMCAS application. Despite the challenges, I applied during my senior year and received multiple interview invitations, including from University of Pennsylvania, Mayo Clinic, and Vanderbilt, and was ultimately accepted to several MD programs. I chose Case Western because it felt like the best fit for me. I have been involved in the medical school admissions process at my institution, where I interviewed applicants and helped evaluate candidates, giving me firsthand insight into how applications are reviewed.</p>
                
                <p>Through these experiences, I came to understand how overwhelming and stressful the medical school application process can be, especially for students without access to guidance or mentorship. This is why I started NafMedPrep: to provide affordable, personalized, and high-quality support to premed students from all backgrounds.</p>
                
                <p>I've worked with many applicants to craft powerful personal statements, optimize AMCAS and AACOMAS applications, build thoughtful school lists, and prepare for interviews with confidence. My goal is to meet students where they are, highlight what makes them unique, and help them present the strongest possible version of their story.</p>
                
                <p>Whether you're a traditional applicant, a reapplicant, or on a non-traditional path, I'm here to support you with clarity, encouragement, and honest feedback.</p>
                
                <p>I look forward to being part of your journey to medicine. Let's get started!</p>
            </div>
          </motion.div>
        </div>
        
        {/* <motion.div 
          className="stats-container"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={3}
          variants={fadeIn}
          ref={statsRef}
        >
          <div className="about-stats">
            <div className="stat-item">
              <h4>{studentsCount}+</h4>
              <p>Students Helped</p>
            </div>
            <div className="stat-item">
              <h4>{acceptanceRate}%</h4>
              <p>Acceptance Rate</p>
            </div>
            <div className="stat-item">
              <h4>{yearsExperience}+</h4>
              <p>Years Experience</p>
            </div>
          </div>
        </motion.div> */}
      </div>
    </section>
  );
};

export default About;
